import { BulkDomainSearchViewModel } from '@orderForm/view-models'
import {
  BulkDomainSearchDomainResultListView,
  BulkDomainSearchSelectedTldListView,
  BulkDomainSearchTldListView
} from '.'

export class BulkDomainSearchBox {
  private submitBtn: HTMLButtonElement
  private validationMessageEl: HTMLSpanElement
  private viewModel: BulkDomainSearchViewModel
  private resultsView: BulkDomainSearchDomainResultListView
  private tldListView: BulkDomainSearchTldListView
  private selectedTldListView: BulkDomainSearchSelectedTldListView

  constructor(
    el: HTMLElement,
    resultsView: BulkDomainSearchDomainResultListView,
    tldListView: BulkDomainSearchTldListView,
    selectedTldListView: BulkDomainSearchSelectedTldListView,
    viewModel: BulkDomainSearchViewModel
  ) {
    // Assign
    this.viewModel = viewModel
    this.resultsView = resultsView
    this.tldListView = tldListView
    this.selectedTldListView = selectedTldListView

    // Init
    const searchBox = el.querySelector<HTMLTextAreaElement>(
      '.js-domain-search__bulk-input'
    )
    const submitBtn = el.querySelector<HTMLButtonElement>(
      '.js-domain-search__bulk-search-btn'
    )
    const validationMessageEl = el.querySelector<HTMLSpanElement>(
      '.js-validation-message'
    )

    // Handle Null Errors

    if (!searchBox) {
      throw new Error('BulkDomainSearchBox: searchBox is null')
    }
    if (!submitBtn) {
      throw new Error('BulkDomainSearchBox: submitBtn is null')
    }
    if (!validationMessageEl) {
      throw new Error('BulkDomainSearchBox: validationMessageEl is null')
    }

    // Assign
    this.submitBtn = submitBtn
    this.validationMessageEl = validationMessageEl

    // Events
    searchBox.addEventListener('paste', this.onSearchBoxPaste.bind(this))
    searchBox.addEventListener('keyup', this.onSearchBoxInput.bind(this))
    this.submitBtn.addEventListener('click', (e) => {
      void this.onSubmitClick.bind(this)(e)
    })
    this.selectedTldListView.onListChange(this.onTldSelected.bind(this))
  }

  private checkIfUserCanSubmit() {
    if (this.viewModel.canSubmit) {
      this.enableBtn()
      this.validationMessageEl.innerHTML = ''
    } else {
      this.disableBtn()
      this.validationMessageEl.innerHTML =
        this.viewModel.validationMessage ?? ''
    }
  }

  private onSearchBoxPaste = (e: ClipboardEvent): void => {
    this.checkIfUserCanSubmit()
    this.tldListView.show()

    const phrasesAsString = e.clipboardData?.getData('text') || ''
    const phrases = phrasesAsString
      .trim()
      .split(/\s+/)
      .filter((phrase) => phrase !== '')

    const existingPhrasesAsString = (<HTMLTextAreaElement>e.target).value
    const existingPhrases = existingPhrasesAsString
      .trim()
      .split(/\s+/)
      .filter((phrase) => phrase !== '')

    this.viewModel.updatePhraseList([...existingPhrases, ...phrases])
  }

  private onSearchBoxInput = (e: Event): void => {
    e.preventDefault()
    this.checkIfUserCanSubmit()
    this.tldListView.show()
    const phrasesAsString = (<HTMLTextAreaElement>e.target).value
    const phrases = phrasesAsString
      .trim()
      .split(/\s+/)
      .filter((phrase) => phrase !== '')
    this.viewModel.updatePhraseList(phrases)
  }

  private onSubmitClick = async (e: Event): Promise<void> => {
    e.preventDefault()
    this.checkIfUserCanSubmit()
    this.disableBtn()
    this.hideSearchText()
    this.showLoading()
    const domains = await this.viewModel.search()
    this.hideLoading()
    this.showSearchText()
    this.enableBtn()
    this.resultsView.show()
    this.tldListView.hide()
    this.resultsView.appendDomains(domains)
  }

  private onTldSelected = (): void => {
    this.checkIfUserCanSubmit()
  }

  private enableBtn() {
    this.submitBtn.removeAttribute('disabled')
  }

  private disableBtn() {
    this.submitBtn.setAttribute('disabled', '')
  }

  private getSearchTextEl(): HTMLElement {
    const searchText =
      this.submitBtn.querySelector<HTMLElement>('.js-search-text')

    if (!searchText) {
      throw new Error('BulkDomainSearchBox: search text el is null')
    }

    return searchText
  }

  private getLoaderEl(): HTMLElement {
    const loader = this.submitBtn.querySelector<HTMLElement>('.loading')

    if (!loader) {
      throw new Error('BulkDomainSearchBox: .loading el is null')
    }

    return loader
  }

  private showLoading(): void {
    const loader = this.getLoaderEl()
    loader.classList.remove('hidden')
  }

  private hideLoading(): void {
    const loader = this.getLoaderEl()
    loader.classList.add('hidden')
  }

  private showSearchText(): void {
    const searchText = this.getSearchTextEl()
    searchText.classList.remove('hidden')
  }

  private hideSearchText(): void {
    const searchText = this.getSearchTextEl()
    searchText.classList.add('hidden')
  }
}
